import React, { useCallback } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  PerformanceAnswersQuery,
  PerformanceQuestionnaireByReviewerStrategyFragment,
  PerformanceReviewerStrategiesStrategyEnum,
} from 'generated/resources/graphql'

import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import AnswersTable, {
  RenderCellArgs,
} from 'modules/performance_process/components/insights/Answers/common/AnswersTable'
import { EmptyState } from 'modules/performance_process/components/insights/Answers/common/EmptyState'
import {
  extractQuestions,
  publishedAnswersWithTargetByReviewType,
} from 'modules/performance_process/components/insights/common/presenters'
import { present } from 'modules/performance_process/components/insights/common/presenters'

export const QuantitativeHeatmapTab: React.FC<{
  answersQuery: PerformanceAnswersQuery
  reviewQuestionnaireByReviewerStrategy: PerformanceQuestionnaireByReviewerStrategyFragment
  reviewType: PerformanceReviewerStrategiesStrategyEnum
}> = ({ answersQuery, reviewQuestionnaireByReviewerStrategy, reviewType }) => {
  const { canUseRichTextEditor: withRichTextEditor, loading } = useRichTextEditor()

  // Get an object for each target with its answers for the selected reviewType
  const publishedAnswersWithTarget = publishedAnswersWithTargetByReviewType(
    answersQuery.performance.reviewProcess?.materializeTargets ?? [],
    reviewType
  )

  const { targets, answers, targetsWithReviewers } = present(
    publishedAnswersWithTarget, // Array of target objects with answers, already filtered by reviewType
    reviewType,
    true
  )

  const numericAnswers = answers.filter((answer) => answer.score != null)

  // Get questions for the selected reviewType
  const quantitativeQuestionsForStrategy = extractQuestions(
    reviewQuestionnaireByReviewerStrategy,
    reviewType
  ).filter(({ answerType }) => answerType === 'rating')

  const renderCell = useCallback(
    (props: RenderCellArgs) => <AnswersTable.ScoreAnswerCell {...props} />,
    []
  )

  if (loading) return null

  if (numericAnswers.length === 0) {
    return (
      <Box flexGrow justifyContent="center">
        <EmptyState
          title={i18n.t('performance_process.insights.answers.empty_state_all_answers')}
        />
      </Box>
    )
  }

  return (
    <AnswersTable
      compact
      collapsable={reviewType === 'peers' || reviewType === 'direct_reports'}
      targets={targets}
      questions={quantitativeQuestionsForStrategy || []}
      answers={numericAnswers}
      targetsWithReviewers={targetsWithReviewers}
      renderCell={renderCell}
      withRichTextEditor={withRichTextEditor}
    />
  )
}
